import styled, { css } from 'styled-components';
import { DefaultTheme } from 'styled-components/dist/types';
import { addPxSuffix } from '../helpers';

interface TypographyProps {
  $color?: keyof DefaultTheme['colors'];
  $weight?: number;
  $maxWidth?: string | number;
  $margin?: string | number;
  $size?: string | number;
  $sizeMobile?: string | number;
  $underline?: boolean;
  $noWrap?: boolean;
}

interface TextProps extends TypographyProps {
  $h1?: boolean;
  $h2?: boolean;
  $h3?: boolean;
  $h4?: boolean;
  $h5?: boolean;
  $h6?: boolean;
  $noWrap?: boolean;
}

interface WysiwygProps extends TypographyProps {
  $h1?: boolean;
  $h2?: boolean;
  $h3?: boolean;
  $h4?: boolean;
  $h5?: boolean;
  $h6?: boolean;
}

export const h1Css = css`
  font-family: Gotham;
  font-size: clamp(24px, 3vw, 34px);
  font-style: normal;
  font-weight: 325;
  line-height: 120%;
  text-align: left;
`;

export const H1 = styled.h1<TypographyProps>`
  ${h1Css}
  font-weight: ${({ $weight }) => $weight ?? 325};
  color: ${({ theme, $color }) => theme.colors[$color ?? 'black']};
  max-width: ${({ $maxWidth }) => addPxSuffix($maxWidth || 'none')};
  margin: ${({ $margin }) => addPxSuffix($margin || '0')};
  font-size: ${({ $size }) => addPxSuffix($size ?? 34)};

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.l}) {
    font-size: ${({ $sizeMobile }) => addPxSuffix($sizeMobile ?? 24)};
  }
`;

export const h2Css = css`
  font-family: Gotham;
  font-size: clamp(24px, 3vw, 28px);
  font-style: normal;
  font-weight: 325;
  line-height: 143%;
  text-align: left;
`;

export const H2 = styled.h2<TypographyProps>`
  ${h2Css}
  font-weight: ${({ $weight }) => $weight ?? 325};
  color: ${({ theme, $color }) => theme.colors[$color ?? 'black']};
  max-width: ${({ $maxWidth }) => addPxSuffix($maxWidth || 'none')};
  margin: ${({ $margin }) => addPxSuffix($margin || '0')};
  font-size: ${({ $size }) => addPxSuffix($size ?? 28)};

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.l}) {
    font-size: ${({ $sizeMobile }) => addPxSuffix($sizeMobile ?? 24)};
  }
`;

export const h3Css = css`
  font-family: Gotham;
  font-size: clamp(20px, 2vw, 24px);
  font-style: normal;
  font-weight: 325;
  line-height: 143%;
  text-align: left;
`;

export const H3 = styled.h3<TypographyProps>`
  ${h3Css}
  font-weight: ${({ $weight }) => $weight ?? 325};
  font-size: ${({ $size }) => addPxSuffix($size ?? 24)};
  color: ${({ theme, $color }) => theme.colors[$color ?? 'black']};
  max-width: ${({ $maxWidth }) => addPxSuffix($maxWidth || 'none')};
  margin: ${({ $margin }) => addPxSuffix($margin || '0')};

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.l}) {
    font-size: ${({ $sizeMobile }) => addPxSuffix($sizeMobile ?? 20)};
  }
`;

export const h4Css = css`
  font-family: Gotham;
  font-size: 18px;
  font-style: normal;
  font-weight: 325;
  line-height: 143%;
  text-align: left;
`;

export const H4 = styled.h4<TypographyProps>`
  ${h4Css}
  font-weight: ${({ $weight }) => $weight ?? 325};
  font-size: ${({ $size }) => addPxSuffix($size ?? 20)};
  color: ${({ theme, $color }) => theme.colors[$color ?? 'black']};
  max-width: ${({ $maxWidth }) => addPxSuffix($maxWidth || 'none')};
  margin: ${({ $margin }) => addPxSuffix($margin || '0')};

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.l}) {
    font-size: ${({ $sizeMobile }) => addPxSuffix($sizeMobile ?? 20)};
  }
`;

export const h5Css = css`
  font-family: Gotham;
  font-size: clamp(14px, 1vw, 20px);
  font-style: normal;
  font-weight: 325;
  line-height: 143%;
  text-align: left;
`;

export const H5 = styled.h5<TypographyProps>`
  ${h5Css}
  font-weight: ${({ $weight }) => $weight ?? 325};
  font-size: ${({ $size }) => addPxSuffix($size ?? 16)};
  color: ${({ theme, $color }) => theme.colors[$color ?? 'black']};
  max-width: ${({ $maxWidth }) => addPxSuffix($maxWidth || 'none')};
  margin: ${({ $margin }) => addPxSuffix($margin || '0')};

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.l}) {
    font-size: ${({ $sizeMobile }) => addPxSuffix($sizeMobile ?? 14)};
  }
`;

export const h6Css = css`
  font-family: Gotham;
  font-size: 14px;
  font-style: normal;
  font-weight: 325;
  line-height: 143%;
  text-align: left;
`;

export const H6 = styled.h6<TypographyProps>`
  ${h6Css}
  font-weight: ${({ $weight }) => $weight ?? 325};
  font-size: ${({ $size }) => addPxSuffix($size ?? 14)};
  color: ${({ theme, $color }) => theme.colors[$color ?? 'black']};
  max-width: ${({ $maxWidth }) => addPxSuffix($maxWidth || 'none')};
  margin: ${({ $margin }) => addPxSuffix($margin || '0')};
`;

export const BreadNormalCss = css`
  font-family: Gotham;
  font-size: clamp(14px, 1vw, 16px);
  font-style: normal;
  font-weight: 325;
  line-height: 156%;
`;

export const BreadNormal = styled.p<TypographyProps>`
  ${BreadNormalCss}
  font-weight: ${({ $weight }) => $weight ?? 325};
  font-size: ${({ $size }) => addPxSuffix($size ?? 'clamp(14px, 1vw, 16px)')};
  color: ${({ theme, $color }) => theme.colors[$color ?? 'black']};
  max-width: ${({ $maxWidth }) => addPxSuffix($maxWidth || 'none')};
  margin: ${({ $margin }) => addPxSuffix($margin || '0')};
`;

export const Text = styled.p<TextProps>`
  ${BreadNormalCss}

  ${({ $h1 }) => $h1 && h1Css}
  ${({ $h2 }) => $h2 && h2Css}
  ${({ $h3 }) => $h3 && h3Css}
  ${({ $h4 }) => $h4 && h4Css}
  ${({ $h5 }) => $h5 && h5Css}
  ${({ $h6 }) => $h6 && h6Css}

  color: ${({ theme, $color }) => theme.colors[$color || 'black']};
  font-weight: ${({ $weight }) => $weight && $weight};
  font-size: ${({ $size }) => $size && addPxSuffix($size)} !important;
  margin: ${({ $margin }) => $margin && addPxSuffix($margin)};
  max-width: ${({ $maxWidth }) => $maxWidth && addPxSuffix($maxWidth)};
  white-space: ${({ $noWrap }) => ($noWrap ? 'nowrap' : 'normal')};

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.l}) {
    font-size: ${({ $sizeMobile }) => addPxSuffix($sizeMobile ?? 14)};
  }
`;

export const BreadSmallCss = css`
  font-family: Gotham;
  font-size: 14px;
  font-style: normal;
  font-weight: 325;
  line-height: 143%;
`;

export const BreadSmall = styled.p<TypographyProps>`
  ${BreadSmallCss}
  font-weight: ${({ $weight }) => $weight ?? 325};
  color: ${({ theme, $color }) => theme.colors[$color ?? 'black']};
  max-width: ${({ $maxWidth }) => addPxSuffix($maxWidth || 'none')};
  margin: ${({ $margin }) => addPxSuffix($margin || '0')};
  font-size: ${({ $size }) => addPxSuffix($size ?? 12)};
`;

export const DetailsBigCss = css`
  font-family: sans-serif;
  font-size: 40px;

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.l}) {
    font-size: 20px;
  }
`;

export const DetailsBig = styled.p<TypographyProps>`
  ${DetailsBigCss}
  font-weight: ${({ $weight }) => $weight ?? 325};
  color: ${({ theme, $color }) => theme.colors[$color ?? 'black']};
  max-width: ${({ $maxWidth }) => addPxSuffix($maxWidth || 'none')};
  margin: ${({ $margin }) => addPxSuffix($margin || '0')};
`;

export const ButtonLabel = styled.p<TypographyProps>`
  color: ${({ theme, $color }) => theme.colors[$color ?? 'black']};
  font-weight: ${({ $weight }) => $weight ?? 325};
  font-family: Gotham;
  font-size: 14px;
  font-style: normal;
  line-height: 142%;
  max-width: ${({ $maxWidth }) => addPxSuffix($maxWidth || 'none')};
  margin: ${({ $margin }) => addPxSuffix($margin || '0')};
`;

export const TextButtonLabel = styled(ButtonLabel)<TypographyProps>`
  font-size: ${({ $size }) => addPxSuffix($size ?? 16)};
  font-weight: ${({ $weight }) => $weight ?? 325};
  width: max-content;
  text-decoration: ${({ $underline }) => ($underline ? 'underline' : 'none')};
`;

export const FormTitle = styled.span<TypographyProps>`
  color: ${({ theme, $color }) => theme.colors[$color ?? 'black']};
  font-family: Gotham;
  font-style: normal;
  line-height: 143%;
  font-size: ${({ $size }) => addPxSuffix($size ?? 14)};
  font-weight: ${({ $weight }) => $weight ?? 400};
`;

export const FormLabel = styled.label<TypographyProps>`
  color: ${({ theme, $color }) => theme.colors[$color ?? 'black']};
  font-family: Gotham;
  font-size: ${({ $size }) => addPxSuffix($size ?? 14)};
  font-style: normal;
  font-weight: ${({ $weight }) => $weight ?? 325};
  line-height: 143%;
  margin: ${({ $margin }) => addPxSuffix($margin || '0')};
  white-space: ${({ $noWrap }) => ($noWrap ? 'nowrap' : 'normal')};
`;

export const WysiwygCss = css<WysiwygProps>`
  p {
    ${BreadNormalCss}

    ${({ $h1 }) => $h1 && h1Css}
    ${({ $h2 }) => $h2 && h2Css}
    ${({ $h3 }) => $h3 && h3Css}
    ${({ $h4 }) => $h4 && h4Css}
    ${({ $h5 }) => $h5 && h5Css}
    ${({ $h6 }) => $h6 && h6Css}
    
    color: ${({ theme, $color }) => theme.colors[$color || 'black']};
    font-weight: ${({ $weight }) => $weight && $weight};
    font-size: ${({ $size }) => $size && addPxSuffix($size)};
    margin: ${({ $margin }) => $margin && addPxSuffix($margin)};
    max-width: ${({ $maxWidth }) => $maxWidth && addPxSuffix($maxWidth)};

    margin: 0 0 19px;

    b {
      font-weight: 350;
    }
  }
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-bottom: 19px;
  }
  h1 {
    ${h1Css}

    strong {
      font-weight: 400;
    }
  }
  h2 {
    ${h2Css}

    strong {
      font-weight: 400;
    }
  }
  h3 {
    ${h3Css}
    strong {
      font-weight: 400;
    }
  }
  h4 {
    ${h4Css}
    strong {
      font-weight: 400;
    }
  }
  h5 {
    ${h5Css}
    strong {
      font-weight: 400;
    }
  }
  h6 {
    ${h6Css}
    strong {
      font-weight: 400;
    }
  }

  ul {
    padding: 0;
    margin: 0;
  }

  li {
    ${BreadNormalCss}
    margin-bottom: 10px;
    margin-left: 40px;
    color: ${({ theme, $color }) => theme.colors[$color || 'black']};

    p {
      margin: 0;
    }
  }

  a {
    color: ${({ theme }) => theme.colors.black};
    text-decoration: none;
    border-bottom: 1px solid ${({ theme }) => theme.colors.black};
    transition: border-bottom 0.2s ease-in-out;
  }

  img {
    max-width: 100%;
    border-radius: 8px;
    margin: 10px 0 30px 0;
  }

  blockquote {
    ${BreadNormalCss}
    margin: 0;
    padding: 0;
    border-left: 4px solid ${({ theme }) => theme.colors.black};
    padding-left: 20px;
  }

  strong {
    font-weight: 350;
  }

  em {
    font: sans-serif italic;
  }
`;

export const Wysiwyg = styled.div<WysiwygProps>`
  ${WysiwygCss}
  max-width: ${({ $maxWidth }) => addPxSuffix($maxWidth || 'none')};
`;
